import window from 'ember-window-mock';
import urlBuilder from 'cio/utils/url-builder';

// The enum value must match the file name of the provider in `oauth-providers`!
export enum Provider {
  GoogleSheets = 'google-sheets',
  GoogleAds = 'google-ads',
  GoogleOIDC = 'google-oidc',
  OktaOIDC = 'okta-oidc',
  AzureOIDC = 'azure-oidc',
  GenericOIDC = 'generic-oidc',
  Slack = 'slack',
  Segment = 'segment',
}

export type OAuthOpenResponse = {
  authorizationCode?: string;
  provider?: string;
  redirectUri?: string;
};

// eslint-disable-next-line spellcheck/spell-checker
const CURRENT_REQUEST_KEY = '__oauth_request';
export const NO_OAUTH_RESPONSE = 'OAuth response not found';

export class OAuthError extends Error {
  provider: Provider;

  constructor(message: string, provider: Provider) {
    super(message);

    this.name = 'OAuthError';
    this.provider = provider;
  }
}

// All OAuth providers need to accept these options
export interface OAuthOpenOptions extends Record<string, unknown> {
  width?: number;
  height?: number;
}

export abstract class OAuthProvider {
  abstract name: Provider;
  protected abstract baseUrl: string;
  protected abstract scope: string;
  protected abstract clientId: string;
  protected abstract extraParams: Record<string, string | boolean | number>;
  protected responseType = 'code';
  private stateId = window.UUID.generate();

  protected get redirectUri() {
    return `${window.location.origin}/oauth2/redirect`;
  }

  // returns object of all query params when closed
  async open({ width, height, ...rest }: OAuthOpenOptions = {}): Promise<OAuthOpenResponse> {
    localStorage.setItem(CURRENT_REQUEST_KEY, this.stateId);

    let url = urlBuilder(this.baseUrl, this.params(rest));
    let popupWidth = width ?? 500;
    let popupHeight = height ?? 500;
    let popup = window.open(
      url,
      '_blank',
      `width=${popupWidth},height=${popupHeight},left=${screen.width / 2 - popupWidth / 2},top=${
        screen.height / 2 - popupHeight / 2
      }`,
    );

    if (popup == null) {
      throw new OAuthError('Unable to open OAuth window', this.name);
    }

    // need to poll for the closed attribute because the `beforeUnload` event
    // only works for same domains. If it's closed before the redirectURL is
    // loaded, then it won't trigger the `beforeUnload` event!
    // https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript
    while (!popup.closed) {
      await new Promise((res) => setTimeout(res, 100));
    }

    let responseUrl = localStorage.getItem(this.stateId);
    localStorage.removeItem(this.stateId);

    if (responseUrl == null) {
      return Promise.reject(new OAuthError(NO_OAUTH_RESPONSE, this.name));
    }

    let res = this.parseResponseUrl(responseUrl);

    if (res.state !== this.stateId) {
      return Promise.reject(new OAuthError("OAuth state response didn't match. Discarding.", this.name));
    }

    if (res[this.responseType] == null) {
      return Promise.reject(new OAuthError('OAuth authorization code not found', this.name));
    }

    return {
      authorizationCode: res[this.responseType],
      redirectUri: this.redirectUri,
      provider: this.name,
    };
  }

  private params(dynamicParams?: Record<string, unknown>) {
    return {
      scope: this.scope,
      redirect_uri: this.redirectUri,
      response_type: this.responseType,
      state: this.stateId,
      client_id: this.clientId,
      ...this.extraParams,
      ...dynamicParams,
    };
  }

  private parseResponseUrl(responseUrl: string) {
    let url = new URL(responseUrl);

    return url.search
      .substring(1)
      .split('&')
      .reduce<Record<string, string>>((acc, pair) => {
        let [key, value] = pair.split('=');

        return {
          ...acc,
          [key]: decodeURIComponent(value),
        };
      }, {});
  }
}
